import type { FC } from 'react';

import type { Company } from '@core/types/api/Company/Company';
import type { Product } from '@core/types/api/Product/Product';
import type { UnmergedDispatchCustomer } from '@core/types/api/UnmergedDispatchCustomer/UnmergedDispatchCustomer';

import { LinkCustomerModal } from '../../Companies/DispatchCustomers/components/LinkCustomerModal';
import { MergeCustomerModal } from '../../Companies/DispatchCustomers/components/MergeCustomerModal';
import { CompanyWithoutDispatchModel } from './CompanyWithoutDispatchModel';
import { DispatchCustomerMismatchModal } from './DispatchCustomerMismatchModal';

type Props = {
  selectedCompany: Company | null;
  selectedCompanyWithMatches: UnmergedDispatchCustomer | null;
  mismatchProducts: Product[];

  isLinkCustomerModalOpen: boolean;
  isMergeCustomerModalOpen: boolean;
  isCompanyWithoutDispatchModalOpen: boolean;
  isDispatchCustomerMismatchModalOpen: boolean;
  isApprovalFlowEnabled?: boolean;

  onSubmit: () => void;
  onSaveAsDraft?: () => void;

  onCloseMerge: () => void;
  onCloseLink: () => void;
  onCloseWithoutDispatch: () => void;
  onCloseMismatch: () => void;
};

export const DispatchCustomerModals: FC<Props> = ({
  selectedCompany,
  selectedCompanyWithMatches,
  mismatchProducts,
  isLinkCustomerModalOpen,
  isMergeCustomerModalOpen,
  isCompanyWithoutDispatchModalOpen,
  isDispatchCustomerMismatchModalOpen,
  isApprovalFlowEnabled = false,
  onSubmit,
  onSaveAsDraft,
  onCloseMerge,
  onCloseLink,
  onCloseWithoutDispatch,
  onCloseMismatch,
}) => {
  const matchObject = selectedCompanyWithMatches?.matchInfo?.[0];
  const companyName = selectedCompanyWithMatches?.name ?? '';
  const linkedDispatchCustomers = selectedCompany?.dispatchCustomers;

  return (
    <>
      {selectedCompanyWithMatches && (
        <>
          <MergeCustomerModal
            onSaveAsDraft={onSaveAsDraft}
            linkedDispatchCustomers={linkedDispatchCustomers}
            onSubmit={onSubmit}
            isOpen={isMergeCustomerModalOpen}
            onClose={onCloseMerge}
            data={selectedCompanyWithMatches}
          />
          {matchObject !== undefined && (
            <LinkCustomerModal
              onSaveAsDraft={onSaveAsDraft}
              alertMessage={`"${companyName}" is not linked to dispatch. Create this customer in your dispatch system, then link them in Slabstack to export.`}
              submitButtonText='Link & Continue'
              companyName={companyName}
              isOpen={isLinkCustomerModalOpen}
              onClose={onCloseLink}
              companyId={selectedCompanyWithMatches?.id}
              onSubmit={onSubmit}
              dispatchCustomer={{
                name: matchObject.dispatchCustomerName,
                integrationType: matchObject.integrationType,
                address: matchObject.address,
                matchStrength: matchObject.matchStrength,
                id: matchObject.dispatchCustomerId,
              }}
            />
          )}
        </>
      )}
      <CompanyWithoutDispatchModel
        onSaveAsDraft={onSaveAsDraft}
        companyName={companyName}
        isOpen={isCompanyWithoutDispatchModalOpen}
        onClose={onCloseWithoutDispatch}
      />
      <DispatchCustomerMismatchModal
        isApprovalFlowEnabled={isApprovalFlowEnabled}
        onSaveAsDraft={onSaveAsDraft}
        isOpen={isDispatchCustomerMismatchModalOpen}
        onClose={onCloseMismatch}
        mismatchProducts={mismatchProducts}
      />
    </>
  );
};
